import Vue from 'vue';
import Vuex from 'vuex';

import { fetchMessageUnreadCount, fetchPlatformConfig } from '@/api/common';
import {
  getAccessToken,
  getRefreshToken,
  removeToken,
  setAccessProactiveRefreshAtFromExpiresIn,
  setAccessToken,
  setRefreshToken
} from '@/utils/auth';
import { clearProactiveAccessRefreshTimer } from '@/utils/tokenRefreshSchedule';
import { resolveFileViewUrl } from '@/utils/fileUrl';

Vue.use(Vuex);

const DEFAULT_PLATFORM_NAME = '体育教师共享平台';

export default new Vuex.Store({
  state: {
    accessToken: getAccessToken(),
    refreshToken: getRefreshToken(),
    platform: {
      name: '',
      logoUrl: '',
      copyright: '',
      seoTitle: ''
    },
    customerService: {
      contact: ''
    },
    platformLoaded: false,
    userBrief: {
      realname: '',
      avatar: ''
    },
    /** 全局消息未读数（message_unread_count，不含资讯/官方通知） */
    messageUnreadCount: 0
  },
  getters: {
    userDisplayName(state) {
      const n = (state.userBrief.realname || '').trim();
      return n || '管理员';
    },
    platformName(state) {
      return state.platform.name || process.env.VUE_APP_TITLE || DEFAULT_PLATFORM_NAME;
    },
    platformLogoUrl(state) {
      return resolveFileViewUrl(state.platform.logoUrl || '');
    },
    platformCopyright(state) {
      return state.platform.copyright || '';
    },
    platformSeoTitle(state) {
      return state.platform.seoTitle || '';
    },
    customerService(state) {
      return state.customerService;
    },
    /** 客服联系方式（platform_config → customer_service.contact） */
    customerServiceContact(state) {
      return (state.customerService.contact || '').trim();
    },
    messageUnreadBadgeText(state) {
      const n = state.messageUnreadCount;
      if (!n || n <= 0) return '';
      return n > 99 ? '99+' : String(n);
    }
  },
  mutations: {
    SET_TOKENS(state, { accessToken, refreshToken }) {
      state.accessToken = accessToken || '';
      state.refreshToken = refreshToken || '';
    },
    SET_ACCESS_TOKEN(state, accessToken) {
      state.accessToken = accessToken || '';
    },
    SET_USER_BRIEF(state, payload) {
      const p = payload || {};
      state.userBrief = {
        realname: p.realname || p.name || '',
        avatar: p.avatar || ''
      };
    },
    CLEAR_USER_BRIEF(state) {
      state.userBrief = { realname: '', avatar: '' };
    },
    SET_MESSAGE_UNREAD_COUNT(state, count) {
      const n = Number(count);
      state.messageUnreadCount = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
    },
    SET_PLATFORM_CONFIG(state, payload) {
      const p = (payload && payload.platform) || {};
      state.platform = {
        name: p.name || '',
        logoUrl: p.logo_url || '',
        copyright: p.copyright || '',
        seoTitle: p.seo_title || ''
      };
      const cs = (payload && payload.customer_service) || {};
      let contact = (cs.contact != null ? String(cs.contact) : '').trim();
      if (!contact) {
        const legacy = [cs.phone, cs.wechat, cs.worktime].filter((s) => s != null && String(s).trim());
        contact = legacy.map((s) => String(s).trim()).join(' ');
      }
      state.customerService = { contact };
      state.platformLoaded = true;
    }
  },
  actions: {
    login({ commit }, payload) {
      const accessToken = payload.accessToken || payload.token || '';
      const refreshToken = payload.refreshToken || '';
      const expiresIn = payload.expiresIn ?? payload.expires_in;
      setAccessToken(accessToken);
      setRefreshToken(refreshToken);
      setAccessProactiveRefreshAtFromExpiresIn(
        expiresIn != null && expiresIn !== '' ? Number(expiresIn) : undefined
      );
      commit('SET_TOKENS', { accessToken, refreshToken });
    },
    logout({ commit }) {
      clearProactiveAccessRefreshTimer();
      removeToken();
      commit('SET_TOKENS', { accessToken: '', refreshToken: '' });
      commit('CLEAR_USER_BRIEF');
      commit('SET_MESSAGE_UNREAD_COUNT', 0);
    },
    applyMessageUnreadCount({ commit }, payload) {
      if (payload != null && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'unread_count')) {
        commit('SET_MESSAGE_UNREAD_COUNT', payload.unread_count);
        return;
      }
      if (typeof payload === 'number') {
        commit('SET_MESSAGE_UNREAD_COUNT', payload);
      }
    },
    async refreshMessageUnreadCount({ commit, state }) {
      if (!state.accessToken) {
        commit('SET_MESSAGE_UNREAD_COUNT', 0);
        return 0;
      }
      try {
        const data = await fetchMessageUnreadCount();
        const n = data && data.unread_count != null ? Number(data.unread_count) : 0;
        commit('SET_MESSAGE_UNREAD_COUNT', n);
        return n;
      } catch {
        return state.messageUnreadCount;
      }
    },
    async loadPlatformConfig({ commit, state }) {
      if (state.platformLoaded) return state.platform;
      try {
        const data = await fetchPlatformConfig();
        commit('SET_PLATFORM_CONFIG', data);
      } catch {
        /* 使用 getter 默认文案 */
      }
      return state.platform;
    }
  }
});
